import { useEffect, useState } from "react";
import { MapPin, Building2, Clock3, ArrowRight, Loader2, X, UploadCloud, CheckCircle2, Send } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import { api, formatApiError } from "@/lib/api";

const emptyForm = { name: "", email: "", phone: "", message: "" };

function ApplyModal({ jobTitle, onClose }) {
  const [form, setForm] = useState(emptyForm);
  const [cv, setCv] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (!cv) er.cv = "Please attach your CV (PDF, DOC or DOCX, max 5 MB).";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("phone", form.phone);
    fd.append("job_title", jobTitle);
    fd.append("message", form.message);
    fd.append("cv", cv);
    setStatus("sending");
    try {
      await api.post("/applications", fd);
      setStatus("success");
    } catch (err) {
      setServerError(formatApiError(err));
      setStatus("idle");
    }
  };

  const inputCls = (k) =>
    `w-full rounded-lg border bg-white px-4 py-3 text-sm text-[#172033] outline-none transition-colors placeholder:text-[#98A2B3] focus:border-[#A87B1F] focus:ring-2 focus:ring-[#A87B1F]/15 ${
      errors[k] ? "border-red-400" : "border-[#0B1F3A]/15"
    }`;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-[#0B1F3A]/55 p-5" onClick={onClose} data-testid="apply-modal-overlay">
      <div className="w-full max-w-lg rounded-2xl bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A87B1F]">Apply</p>
            <h2 className="mt-1 font-display text-xl font-bold text-[#0B1F3A]">{jobTitle}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="apply-modal-close" className="rounded-md p-1.5 text-[#667085] hover:bg-[#F5F8FC]">
            <X size={18} />
          </button>
        </div>

        {status === "success" ? (
          <div className="flex flex-col items-start gap-3 py-8" data-testid="apply-success">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#A87B1F]/10 text-[#A87B1F]">
              <CheckCircle2 size={24} />
            </span>
            <h3 className="font-display text-lg font-bold text-[#0B1F3A]">Application sent</h3>
            <p className="text-sm leading-relaxed text-[#667085]">
              Thank you — your application and CV have been received. A confirmation email is on its way to you.
            </p>
            <button onClick={onClose} data-testid="apply-done-button" className="mt-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="apply-form">
            <div>
              <label htmlFor="ap-name" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Full name *</label>
              <input id="ap-name" className={inputCls("name")} value={form.name} onChange={set("name")} data-testid="apply-input-name" placeholder="Your full name" />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="ap-email" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Email *</label>
                <input id="ap-email" type="email" className={inputCls("email")} value={form.email} onChange={set("email")} data-testid="apply-input-email" placeholder="you@example.co.za" />
                {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="ap-phone" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Phone</label>
                <input id="ap-phone" className={inputCls("phone")} value={form.phone} onChange={set("phone")} data-testid="apply-input-phone" placeholder="+27 ..." />
              </div>
            </div>
            <div>
              <label htmlFor="ap-message" className="mb-1.5 block text-[13px] font-semibold text-[#172033]">Message</label>
              <textarea id="ap-message" rows={3} className={inputCls("message")} value={form.message} onChange={set("message")} data-testid="apply-input-message" placeholder="Anything you'd like us to know (optional)" />
            </div>
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold text-[#172033]">CV *</label>
              <label
                htmlFor="ap-cv"
                className={`flex cursor-pointer items-center gap-3 rounded-lg border border-dashed px-4 py-4 text-sm transition-colors hover:border-[#A87B1F] ${errors.cv ? "border-red-400" : "border-[#0B1F3A]/20"}`}
                data-testid="apply-cv-dropzone"
              >
                <UploadCloud size={20} className="shrink-0 text-[#A87B1F]" />
                <span className={cv ? "font-medium text-[#172033]" : "text-[#667085]"}>
                  {cv ? cv.name : "Choose your CV — PDF, DOC or DOCX, max 5 MB"}
                </span>
              </label>
              <input
                id="ap-cv" type="file" accept=".pdf,.doc,.docx" className="hidden"
                data-testid="apply-input-cv"
                onChange={(e) => { setCv(e.target.files?.[0] || null); setErrors((er) => ({ ...er, cv: undefined })); }}
              />
              {errors.cv && <p className="mt-1 text-xs text-red-500">{errors.cv}</p>}
            </div>
            {serverError && <p className="text-sm text-red-500" data-testid="apply-server-error">{serverError}</p>}
            <button
              type="submit" disabled={status === "sending"} data-testid="apply-submit-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B1F3A] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#A87B1F] disabled:opacity-60"
            >
              {status === "sending" ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
              {status === "sending" ? "Sending…" : "Submit Application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Careers() {
  const [jobs, setJobs] = useState(null);
  const [applyFor, setApplyFor] = useState(null); // job title string or null

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  return (
    <>
      <Seo
        title="Careers | Incremental Consulting"
        description="Join Incremental Consulting. View current openings in programme delivery, PMO and delivery resourcing."
      />
      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Careers"
            title="Build Better Business, One Step at a Time"
            sub="We look for people who combine structured thinking with practical delivery. Current openings are listed below."
            testId="careers-page"
          />
          <div className="mt-14" data-testid="careers-list">
            {jobs === null ? (
              <div className="flex items-center gap-2 text-sm text-[#667085]"><Loader2 size={16} className="animate-spin" /> Loading openings…</div>
            ) : jobs.length === 0 ? (
              <Reveal>
                <div className="rounded-xl border border-dashed border-[#A87B1F]/30 bg-white p-10" data-testid="careers-empty-state">
                  <h2 className="font-display text-lg font-bold text-[#0B1F3A]">No open positions right now</h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#667085]">
                    We are always interested in hearing from strong business analysts and consultants. Send us your CV and we will keep you in mind for future opportunities.
                  </p>
                  <button onClick={() => setApplyFor("Speculative Application")} data-testid="careers-speculative-link" className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">
                    Send a speculative application
                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                </div>
              </Reveal>
            ) : (
              <div className="space-y-5">
                {jobs.map((job, i) => (
                  <Reveal key={job.id} delay={i * 0.08}>
                    <article className="rounded-xl border border-[#0B1F3A]/10 bg-white p-7 transition-all duration-300 hover:border-[#A87B1F]/40 hover:shadow-[0_16px_36px_-18px_rgba(21,101,192,0.3)]" data-testid={`careers-job-${job.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h2 className="font-display text-xl font-bold text-[#0B1F3A]">{job.title}</h2>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-[#667085]">
                            {job.department && <span className="inline-flex items-center gap-1.5"><Building2 size={14} /> {job.department}</span>}
                            {job.location && <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {job.location}</span>}
                            {job.employment_type && <span className="inline-flex items-center gap-1.5"><Clock3 size={14} /> {job.employment_type}</span>}
                          </div>
                        </div>
                        <button
                          onClick={() => setApplyFor(job.title)}
                          data-testid={`careers-apply-${job.id}`}
                          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#0B1F3A] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#A87B1F]"
                        >
                          Apply <ArrowRight size={14} />
                        </button>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-[#667085]">{job.summary}</p>
                      {job.description && (
                        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#172033]/80">{job.description}</p>
                      )}
                    </article>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <CtaBand />
      {applyFor && <ApplyModal jobTitle={applyFor} onClose={() => setApplyFor(null)} />}
    </>
  );
}
